import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  lmsApi,
  type MyTaskItem,
  type TaskAssignmentView,
  type TaskAttachment,
  type TaskRecord,
} from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { formatDayLong, formatRelative, formatTime } from './dates';

/** Everything the window can show; a student's copy simply has no assignments. */
export interface TaskModalTask extends TaskRecord {
  teacherName?: string | null;
  assignments?: TaskAssignmentView[];
}

export interface TaskModalProps {
  task: TaskModalTask;
  /** The viewer's own completion, when the task was set for them. */
  myDone?: boolean | null;
  /** Student's live assignment row — drives the solution panel. */
  myItem?: MyTaskItem | null;
  onToggleDone?: (done: boolean) => void | Promise<void>;
  /** Called after the student uploads files or creates a solution board. */
  onSubmissionChange?: (patch: Partial<MyTaskItem>) => void;
  /** Offered to whoever may change the task — its author or the owner. */
  onEdit?: () => void;
  /** Owner/teacher ticking a student's row as accepted. */
  onMarkStudent?: (studentId: string, done: boolean) => void;
  onOpenBoard?: (boardId: string) => void;
  onClose: () => void;
}

const isImage = (file: TaskAttachment) => file.mime.startsWith('image/');

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

/**
 * The whole assignment, opened from a task list or from the calendar.
 *
 * For a student this is also where the solution is handed in — photos, files
 * and a dedicated board — so the teacher can jump from the same window
 * straight to that board when checking.
 */
export function TaskModal({
  task,
  myDone,
  myItem,
  onToggleDone,
  onSubmissionChange,
  onEdit,
  onMarkStudent,
  onOpenBoard,
  onClose,
}: TaskModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const photos = task.attachments.filter(isImage);
  const files = task.attachments.filter((file) => !isImage(file));
  const overdue = task.dueAt !== null && !myDone && new Date(task.dueAt).getTime() < Date.now();

  return (
    <div
      className="modal-backdrop"
      onPointerDown={(e) => {
        if (!dialogRef.current?.contains(e.target as Node)) onClose();
      }}
    >
      <div
        className="task-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={task.title}
      >
        <header className="task-modal-head">
          <div className="task-modal-heading">
            {task.subject && <span className="subject-badge">{task.subject}</span>}
            <h2>{task.title}</h2>
          </div>
          <button type="button" className="icon-btn" aria-label="Закрыть" onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </header>

        <div className="task-modal-meta">
          {task.dueAt ? (
            <span className={`task-due ${overdue ? 'is-overdue' : ''}`}>
              <Icon name="clock" size={13} />
              Срок: {formatDayLong(new Date(task.dueAt))}, {formatTime(task.dueAt)}
              <em>({formatRelative(task.dueAt)})</em>
            </span>
          ) : (
            <span className="task-due">
              <Icon name="clock" size={13} />
              Без срока
            </span>
          )}
          {task.teacherName && (
            <span className="task-author">
              <Icon name="graduation" size={13} />
              {task.teacherName}
            </span>
          )}
        </div>

        <div className="task-modal-body">
          {task.description ? (
            <p className="task-modal-desc">{task.description}</p>
          ) : (
            <p className="org-members-hint">Описание не добавлено.</p>
          )}

          {photos.length > 0 && (
            <div className="task-photos">
              {photos.map((photo) => (
                <a
                  key={photo.id}
                  href={photo.url}
                  target="_blank"
                  rel="noreferrer"
                  title={photo.name}
                >
                  <img src={photo.url} alt={photo.name} loading="lazy" />
                </a>
              ))}
            </div>
          )}

          {files.length > 0 && (
            <ul className="attachment-list">
              {files.map((file) => (
                <li key={file.id}>
                  <a href={file.url} target="_blank" rel="noreferrer">
                    <Icon name="paperclip" size={13} />
                    {file.name}
                    <em className="attachment-size">{formatSize(file.bytes)}</em>
                  </a>
                </li>
              ))}
            </ul>
          )}

          {myItem && (
            <StudentSolutionPanel
              orgId={task.orgId}
              taskId={task.id}
              item={myItem}
              onSubmissionChange={onSubmissionChange}
              onOpenBoard={onOpenBoard}
            />
          )}

          {task.assignments && task.assignments.length > 0 && (
            <div className="task-progress">
              <p className="popover-label">
                Кому задано · выполнили {task.assignments.filter((a) => a.done).length} из{' '}
                {task.assignments.length}
              </p>
              <ul className="task-progress-list">
                {task.assignments.map((student) => (
                  <li
                    key={student.userId}
                    className={student.done ? 'is-done' : student.needsReview ? 'is-review' : ''}
                  >
                    {onMarkStudent && (
                      <label className="day-done-check">
                        <input
                          type="checkbox"
                          checked={student.done}
                          aria-label={
                            student.done
                              ? `Снять сдачу: ${student.name}`
                              : `Принять работу: ${student.name}`
                          }
                          onChange={(e) => onMarkStudent(student.userId, e.target.checked)}
                        />
                      </label>
                    )}
                    <span className="org-member-avatar" style={{ background: student.color }}>
                      {student.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="task-progress-name">{student.name}</span>
                    <span className="task-progress-actions">
                      {student.needsReview && (
                        <span className="hw-tone is-soon">на проверке</span>
                      )}
                      {student.hasSubmission && !student.boardId && !student.needsReview && (
                        <span className="hw-tone is-soon">есть файлы</span>
                      )}
                      {student.boardId && onOpenBoard ? (
                        <button
                          type="button"
                          className="ghost-btn hw-check-btn"
                          onClick={() => onOpenBoard(student.boardId!)}
                        >
                          <Icon name="boards" size={13} />
                          {student.boardName ? `К решению · ${student.boardName}` : 'Проверить'}
                        </button>
                      ) : student.done ? (
                        <span className="task-progress-state is-done">
                          <Icon name="check" size={12} />
                          сдано
                        </span>
                      ) : student.needsReview ? null : (
                        <span className="task-progress-state">не сдано</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              {task.assignments.some((a) => (a.submissionAttachments?.length ?? 0) > 0) && (
                <div className="task-teacher-subs">
                  {task.assignments
                    .filter((a) => (a.submissionAttachments?.length ?? 0) > 0)
                    .map((student) => (
                      <div key={`sub-${student.userId}`} className="task-teacher-sub">
                        <p className="popover-label">Файлы решения · {student.name}</p>
                        <div className="task-photos">
                          {(student.submissionAttachments ?? [])
                            .filter(isImage)
                            .map((photo) => (
                              <a
                                key={photo.id}
                                href={photo.url}
                                target="_blank"
                                rel="noreferrer"
                                title={photo.name}
                              >
                                <img src={photo.url} alt={photo.name} loading="lazy" />
                              </a>
                            ))}
                        </div>
                        <ul className="attachment-list">
                          {(student.submissionAttachments ?? [])
                            .filter((f) => !isImage(f))
                            .map((file) => (
                              <li key={file.id}>
                                <a href={file.url} target="_blank" rel="noreferrer">
                                  <Icon name="paperclip" size={13} />
                                  {file.name}
                                </a>
                              </li>
                            ))}
                        </ul>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="task-modal-foot">
          {onToggleDone && (
            <label className="task-done-toggle">
              <input
                type="checkbox"
                checked={myDone === true}
                // Once the teacher has accepted it, reopening is their call.
                disabled={myItem?.accepted === true}
                onChange={(e) => void onToggleDone(e.target.checked)}
              />
              <span>
                {myItem?.accepted
                  ? 'Принято учителем'
                  : myDone
                    ? myItem?.accepted === false
                      ? 'Сдано · на проверке'
                      : 'Сдано'
                    : 'Отметить сданным'}
              </span>
            </label>
          )}
          <span className="spacer" />
          {onEdit && (
            <button type="button" className="ghost-btn" onClick={onEdit}>
              <Icon name="pencil" size={15} />
              Изменить
            </button>
          )}
          <button type="button" className="primary-btn" onClick={onClose}>
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  );
}

function StudentSolutionPanel({
  orgId,
  taskId,
  item,
  onSubmissionChange,
  onOpenBoard,
}: {
  orgId: string;
  taskId: string;
  item: MyTaskItem;
  onSubmissionChange?: (patch: Partial<MyTaskItem>) => void;
  onOpenBoard?: (boardId: string) => void;
}) {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  /** A file is being removed; with `uploading`, the one "a save is running" flag. */
  const [removing, setRemoving] = useState(false);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = uploading || removing;

  const submission = item.submissionAttachments ?? [];
  /*
   * Every save sends the whole list, so it has to be built from the newest
   * one. The `submission` a handler closed over is as old as the render it
   * came from: removing two files in a row would send the second request with
   * the first file still in it, bringing it back.
   */
  const latest = useRef(submission);
  useEffect(() => {
    latest.current = item.submissionAttachments ?? [];
  }, [item.submissionAttachments]);
  /** Set synchronously — `busy` only disables the buttons a render later. */
  const saving = useRef(false);
  const submissionPhotos = submission.filter(isImage);
  const submissionFiles = submission.filter((file) => !isImage(file));

  const persist = async (patch: {
    submissionAttachments?: TaskAttachment[];
    boardId?: string | null;
    done?: boolean;
  }) => {
    if (!user) return;
    const saved = await lmsApi.submitAssignment(orgId, taskId, user.id, patch);
    latest.current = saved.submissionAttachments ?? [];
    onSubmissionChange?.({
      submissionAttachments: saved.submissionAttachments ?? [],
      boardId: saved.boardId,
      boardName: saved.boardName,
      submittedAt: saved.submittedAt,
      done: saved.done,
      accepted: saved.accepted,
      hasSubmission:
        (saved.submissionAttachments?.length ?? 0) > 0 ||
        saved.boardId !== null ||
        saved.done,
    });
  };

  const upload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || saving.current) return;
    saving.current = true;
    setUploading(true);
    setError(null);
    try {
      const uploaded: TaskAttachment[] = [];
      for (const file of Array.from(fileList)) {
        uploaded.push(await lmsApi.uploadTaskAttachment(file));
      }
      await persist({ submissionAttachments: [...latest.current, ...uploaded] });
    } catch {
      setError('Не удалось загрузить файл');
    } finally {
      saving.current = false;
      setUploading(false);
    }
  };

  const removeFile = async (id: string) => {
    if (saving.current) return;
    saving.current = true;
    setRemoving(true);
    setError(null);
    try {
      await persist({ submissionAttachments: latest.current.filter((f) => f.id !== id) });
    } catch {
      setError('Не удалось убрать файл');
    } finally {
      saving.current = false;
      setRemoving(false);
    }
  };

  const createBoard = async () => {
    if (!user) return;
    setCreatingBoard(true);
    setError(null);
    try {
      const result = await lmsApi.createSolutionBoard(orgId, taskId, user.id);
      onSubmissionChange?.({
        boardId: result.assignment.boardId,
        boardName: result.assignment.boardName,
        submittedAt: result.assignment.submittedAt,
        hasSubmission: true,
      });
      onOpenBoard?.(result.board.id);
    } catch {
      setError('Не удалось создать доску');
    } finally {
      setCreatingBoard(false);
    }
  };

  return (
    <section className="task-solution">
      <header className="task-solution-head">
        <h3>
          <Icon name="paperclip" size={15} />
          Моё решение
        </h3>
        <p>Загрузите фото или документ — либо создайте доску, где будете решать задание.</p>
      </header>

      {item.boardId ? (
        <div className="task-board-mark">
          <span className="task-board-mark-icon" aria-hidden="true">
            <Icon name="boards" size={18} />
          </span>
          <div className="task-board-mark-text">
            <strong>Доска решения</strong>
            <em>{item.boardName ?? 'Домашняя работа'}</em>
          </div>
          {onOpenBoard && (
            <button type="button" className="primary-btn" onClick={() => onOpenBoard(item.boardId!)}>
              Открыть
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="task-board-create"
          disabled={creatingBoard}
          onClick={() => void createBoard()}
        >
          <Icon name="boards" size={18} />
          <span>
            <strong>{creatingBoard ? 'Создаём доску…' : 'Создать доску домашней работы'}</strong>
            <em>Учитель сможет сразу перейти к вашему решению</em>
          </span>
        </button>
      )}

      {(submissionPhotos.length > 0 || submissionFiles.length > 0) && (
        <>
          {submissionPhotos.length > 0 && (
            <div className="task-photos">
              {submissionPhotos.map((photo) => (
                <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer" title={photo.name}>
                  <img src={photo.url} alt={photo.name} loading="lazy" />
                </a>
              ))}
            </div>
          )}
          <ul className="attachment-list">
            {submission.map((file) => (
              <li key={file.id}>
                <a href={file.url} target="_blank" rel="noreferrer">
                  <Icon name="paperclip" size={13} />
                  {file.name}
                  <em className="attachment-size">{formatSize(file.bytes)}</em>
                </a>
                <button
                  type="button"
                  aria-label={`Убрать ${file.name}`}
                  disabled={busy}
                  onClick={() => void removeFile(file.id)}
                >
                  <Icon name="close" size={13} />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="task-solution-actions">
        <button
          type="button"
          className="ghost-btn"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          <Icon name="paperclip" size={15} />
          {uploading ? 'Загрузка…' : 'Прикрепить фото или файл'}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp"
          hidden
          onChange={async (e) => {
            const list = e.target.files;
            e.target.value = '';
            await upload(list);
          }}
        />
      </div>

      {error && <p className="form-error">{error}</p>}
    </section>
  );
}
